import React, { useEffect, useState, useCallback } from 'react';
import { approvals as approvalsApi } from '../../lib/api';
import { formatApiDateTime } from '../../lib/format';
import { useNotify } from '../../components/NotifyProvider';
import { useLocale } from '../../i18n/LocaleContext';
import CommentApprovalCard from './approvals/CommentApprovalCard';
import {
  Loader2, Check, X, Clock, Edit3, Sparkles,
  MessageSquare, FileText, Image as ImageIcon, Calendar,
  Globe, Users, BarChart2, Video as VideoIcon, ExternalLink, AlertTriangle, Paperclip, Trash2,
} from 'lucide-react';

const TABS = [
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
];

const SECTIONS = [
  { key: 'posts', label: 'Posts', Icon: FileText },
  { key: 'messages', label: 'Replies', Icon: MessageSquare },
];

const APPROVALS_SESSION_KEY = 'linirio.approvals.filters';

function initialApprovalFilters() {
  try {
    const saved = JSON.parse(window.sessionStorage.getItem(APPROVALS_SESSION_KEY) || '{}');
    return {
      section: ['posts', 'messages'].includes(saved.section) ? saved.section : 'messages',
      replyKind: ['inbox', 'comments'].includes(saved.replyKind) ? saved.replyKind : 'inbox',
      tab: ['pending', 'approved', 'rejected'].includes(saved.tab) ? saved.tab : 'pending',
    };
  } catch (_) {
    return { section: 'messages', replyKind: 'inbox', tab: 'pending' };
  }
}

export default function Approvals() {
  const notify = useNotify();
  const { t } = useLocale();
  const [initialFilters] = useState(initialApprovalFilters);
  const [section, setSection] = useState(initialFilters.section);
  const [replyKind, setReplyKind] = useState(initialFilters.replyKind);
  const [items, setItems] = useState([]);
  const [commentItems, setCommentItems] = useState([]);
  const [postItems, setPostItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(initialFilters.tab);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [attachmentFiles, setAttachmentFiles] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [rejectingPostId, setRejectingPostId] = useState(null);
  const [postRejectComment, setPostRejectComment] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      if (section === 'messages') {
        if (replyKind === 'comments') {
          const data = await approvalsApi.listComments(tab);
          setCommentItems(data.items || []);
        } else {
          const data = await approvalsApi.list(tab);
          setItems(data.items || []);
        }
      } else {
        const data = await approvalsApi.listPosts(tab);
        setPostItems(data.items || []);
      }
    } finally {
      setLoading(false);
    }
  }, [replyKind, section, tab]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    window.sessionStorage.setItem(
      APPROVALS_SESSION_KEY,
      JSON.stringify({ section, replyKind, tab }),
    );
  }, [section, replyKind, tab]);

  const decide = async (id, decision, edited) => {
    setBusyId(id);
    try {
      const attachmentFile = attachmentFiles[id];
      if (attachmentFile) {
        await approvalsApi.decideWithAttachment(id, { decision, edited_text: edited, file: attachmentFile });
      } else {
        await approvalsApi.decide(id, decision, edited);
      }
      await load();
      window.dispatchEvent(new CustomEvent('linirio:approvals-changed'));
      window.dispatchEvent(new CustomEvent('linirio:tasks-changed'));
      setEditingId(null);
      setEditText('');
      setAttachmentFiles((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      notify.success(
        decision === 'approve' ? 'Reply approved' : 'Reply rejected',
        decision === 'approve' ? 'The reply has been sent.' : 'The draft has been discarded.',
      );
    } catch (err) {
      notify.error('Action failed', err.message || 'Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  const decidePost = async (id, decision, rejection_comment = null) => {
    if (decision === 'reject' && !rejection_comment?.trim()) {
      notify.error('Comment required', 'Please describe what should be changed.');
      return;
    }
    setBusyId(id);
    try {
      const res = await approvalsApi.decidePost(id, { decision, rejection_comment });
      await load();
      window.dispatchEvent(new CustomEvent('linirio:approvals-changed'));
      setEditingId(null);
      setEditText('');
      setRejectingPostId(null);
      setPostRejectComment('');
      if (decision === 'reject') {
        notify.success('Post rejected', 'Your team has been notified.');
      } else if (res?.status === 'published') {
        notify.success('Published to LinkedIn 🎉', res?.url ? 'View it live on your profile.' : 'Live on your profile.', {
          actionLabel: res?.url ? 'Open post' : undefined,
          onAction: res?.url ? () => window.open(res.url, '_blank') : undefined,
          autoClose: false,
        });
      } else if (res?.status === 'approved') {
        notify.success(
          'Approved · scheduled',
          "We'll publish it automatically at the scheduled time.",
        );
      } else if (res?.publish_failed) {
        notify.error('Approved, but publishing failed', res.error || 'Your team has been notified.');
      } else {
        notify.success('Saved', 'Your decision has been recorded.');
      }
    } catch (err) {
      notify.error('Action failed', err.message || 'Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  const decideComment = async (id, decision, editedText = null) => {
    setBusyId(id);
    try {
      await approvalsApi.decideComment(id, decision, editedText);
      await load();
      window.dispatchEvent(new CustomEvent('linirio:approvals-changed'));
      notify.success(decision === 'approve' ? t('Comment ready to copy') : t('Comment rejected'));
    } catch (err) {
      notify.error(t('Action failed'), err.message || t('Please try again.'));
    } finally {
      setBusyId(null);
    }
  };

  const markCommentPosted = async (id) => {
    setBusyId(id);
    try {
      await approvalsApi.markCommentPosted(id);
      await load();
      window.dispatchEvent(new CustomEvent('linirio:approvals-changed'));
      notify.success(t('Comment marked as posted'));
    } catch (err) {
      notify.error(t('Action failed'), err.message || t('Please try again.'));
    } finally {
      setBusyId(null);
    }
  };

  const saveCommentDraft = async (id, selectedVariantId, editedText) => {
    try {
      const updated = await approvalsApi.updateCommentDraft(id, {
        selected_variant_id: selectedVariantId,
        edited_text: editedText,
      });
      setCommentItems((current) => current.map((item) => (item.id === id ? { ...item, ...updated } : item)));
      return updated;
    } catch (err) {
      notify.error(t('Could not save the draft.'), err.message || t('Please try again.'));
      throw err;
    }
  };

  const list = section === 'messages' ? (replyKind === 'comments' ? commentItems : items) : postItems;

  return (
    <div className="app-page-shell bg-[#F7F8F6]" data-testid="approvals-page">
      <header className="shrink-0 border-b border-[#D7DCE4] flex flex-col gap-3 px-4 py-3 sm:h-14 sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:py-0 sticky top-0 bg-[#F7F8F6]/95 backdrop-blur z-10">
        <div className="flex w-full items-center justify-between gap-3 sm:w-auto sm:justify-start">
          <h1 className="text-lg font-semibold text-[#0B1D3A]">Approvals</h1>
          <span className="shrink-0 text-xs text-[#6B7280]">{list.length} {tab}</span>
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
          <div className="grid w-full grid-cols-2 gap-1 bg-white border border-[#D7DCE4] rounded-xl p-0.5 sm:flex sm:w-auto">
            {SECTIONS.map((s) => (
              <button
                key={s.key}
                onClick={() => setSection(s.key)}
                className={`inline-flex min-w-0 items-center justify-center gap-1.5 whitespace-nowrap px-3 py-1.5 text-xs font-medium rounded-md sm:py-1 ${
                  section === s.key ? 'bg-[#E6FAF9] text-[#0B1D3A] shadow-sm' : 'text-[#6B7280]'
                }`}
                data-testid={`approvals-section-${s.key}`}
              >
                <s.Icon className="w-3.5 h-3.5" /> {s.label}
              </button>
            ))}
          </div>
          {section === 'messages' && (
            <div className="flex w-max shrink-0 flex-nowrap gap-1 rounded-xl border border-[#D7DCE4] bg-white p-0.5">
              {[['inbox', 'Inbox'], ['comments', 'Post comments']].map(([key, label]) => (
                <button key={key} onClick={() => setReplyKind(key)} className={`whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium sm:py-1 ${replyKind === key ? 'bg-[#FFF1DE] text-[#9A4D00]' : 'text-[#6B7280]'}`}>{t(label)}</button>
              ))}
            </div>
          )}
          <div className="grid w-full grid-cols-3 gap-1 bg-white border border-[#D7DCE4] rounded-xl p-0.5 sm:flex sm:w-auto">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`min-w-0 whitespace-nowrap px-2 py-1.5 text-xs font-medium rounded-md sm:px-3 sm:py-1 ${
                  tab === t.key ? 'bg-[#12C8C6] text-white shadow-sm' : 'text-[#6B7280]'
                }`}
                data-testid={`approvals-tab-${t.key}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <div className="app-page-scroll">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-[#9B9B9B]" />
          </div>
        ) : list.length === 0 ? (
          <div className="text-center py-24">
            <div className="text-5xl mb-4 select-none">{section === 'posts' ? '📥' : '✨'}</div>
            <h3 className="text-base font-semibold text-[#191919]">Nothing here yet</h3>
            <p className="text-[13px] text-[#6B6B6B] mt-1.5 max-w-sm mx-auto">
              {section === 'posts'
                ? (tab === 'pending'
                    ? "Posts your team drafts on your behalf will land here for your review. You'll also receive an email link for each one."
                    : 'No posts in this state yet.')
                : (tab === 'pending'
                    ? 'AI-drafted replies show up here after the quiet window passes when approval mode is enabled in Settings.'
                    : 'No replies in this state.')}
            </p>
          </div>
        ) : section === 'posts' ? (
          <div className="divide-y divide-[#E3E2E0]">
            {postItems.map((p, idx) => (
              <div key={p.id} className={idx === 0 ? 'pb-8' : 'py-8 first:pt-0 last:pb-0'}>
                <PostApprovalCard
                  p={p}
                  tab={tab}
                  busyId={busyId}
                  rejectingPostId={rejectingPostId}
                  postRejectComment={postRejectComment}
                  setRejectingPostId={setRejectingPostId}
                  setPostRejectComment={setPostRejectComment}
                  onDecide={decidePost}
                />
              </div>
            ))}
          </div>
        ) : replyKind === 'comments' ? (
          <div className="space-y-4">
            {commentItems.map((item) => (
              <CommentApprovalCard key={item.id} item={item} status={tab} busy={busyId === item.id} onDecision={decideComment} onSaveDraft={saveCommentDraft} onMarkPosted={markCommentPosted} t={t} />
            ))}
          </div>
        ) : (
          items.map((it) => (
            <article
              key={it.id}
              className="border border-[#D7DCE4] rounded-2xl bg-white p-4 shadow-[0_18px_34px_-28px_rgba(11,29,58,0.22)]"
              data-testid={`approval-${it.id}`}
            >
              <header className="flex items-center gap-3 mb-3">
                {it.lead_picture ? (
                  <img src={it.lead_picture} alt="" className="w-9 h-9 rounded-full object-cover" />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-[#E6FAF9] flex items-center justify-center text-sm font-medium text-[#0B1D3A]">
                    {(it.lead_name || '?').charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#191919] truncate">{it.lead_name || 'Unknown'}</p>
                  <p className="text-[11px] text-[#6B6B6B] flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {it.created_at ? formatApiDateTime(it.created_at) : ''} · confidence{' '}
                    {Math.round((it.confidence || 0) * 100)}%
                  </p>
                </div>
                {tab !== 'pending' && (
                  <span
                    className={`text-[11px] px-2 py-0.5 rounded-md border ${
                      tab === 'approved'
                        ? 'bg-[#EEFBDD] text-[#376B2B] border-[#A8E89C]'
                        : 'bg-[#FFEBE9] text-[#CE3636] border-[#FF8080]'
                    }`}
                  >
                    {tab}
                  </span>
                )}
              </header>

              <div className="rounded-2xl border border-[#12C8C6] bg-[#E6FAF9] p-3 mb-3">
                {it.source_message_text && (
                  <div className="mb-3 rounded-xl border border-[#D7DCE4] bg-white p-2.5">
                    <p className="text-[11px] font-semibold text-[#6B7280] mb-1 flex items-center gap-1.5">
                      <MessageSquare className="w-3 h-3" /> Last inbound message
                    </p>
                    <p className="text-sm text-[#0B1D3A] whitespace-pre-wrap">{it.source_message_text}</p>
                    {it.source_message_at && (
                      <p className="text-[11px] text-[#6B7280] mt-1">{formatApiDateTime(it.source_message_at)}</p>
                    )}
                  </div>
                )}
                <p className="text-[11px] font-semibold text-[#0B1D3A] mb-1 flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3" /> AI draft
                </p>
                {editingId === it.id ? (
                  <div className="space-y-2">
                    <textarea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      rows={3}
                      className="w-full bg-white border border-[#D7DCE4] rounded-xl text-sm p-2 focus:outline-none focus:ring-2 focus:ring-[#12C8C6]/20"
                      data-testid={`approval-edit-${it.id}`}
                    />
                    <div className="flex flex-wrap items-center gap-2">
                      <label className="inline-flex items-center gap-1 px-3 py-1.5 border border-[#D7DCE4] text-sm rounded-xl hover:bg-white cursor-pointer">
                        <Paperclip className="w-3.5 h-3.5" />
                        Add attachment
                        <input
                          type="file"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0] || null;
                            setAttachmentFiles((prev) => ({ ...prev, [it.id]: file }));
                          }}
                        />
                      </label>
                      {attachmentFiles[it.id] ? (
                        <div className="inline-flex items-center gap-2 text-xs px-2 py-1 rounded-xl bg-white text-[#0B1D3A] border border-[#D7DCE4]">
                          <Paperclip className="w-3 h-3" />
                          <span>{attachmentFiles[it.id].name}</span>
                          <button
                            type="button"
                            onClick={() => {
                              setAttachmentFiles((prev) => {
                                const next = { ...prev };
                                delete next[it.id];
                                return next;
                              });
                            }}
                            className="text-[#6B6B6B] hover:text-[#191919]"
                            data-testid={`remove-approval-attachment-${it.id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-[#0B1D3A] whitespace-pre-wrap">{it.sent_text || it.draft}</p>
                )}
              </div>

              {tab === 'pending' && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => decide(it.id, 'approve', editingId === it.id ? editText : null)}
                    disabled={busyId === it.id}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#12C8C6] hover:bg-[#0FB3B1] text-white text-sm font-medium rounded-xl disabled:opacity-50"
                    data-testid={`approve-${it.id}`}
                  >
                    {busyId === it.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                    Approve & send
                  </button>
                  <button
                    onClick={() => {
                      if (editingId === it.id) {
                        setEditingId(null);
                        setEditText('');
                      } else {
                        setEditingId(it.id);
                        setEditText(it.draft || '');
                      }
                    }}
                    className="inline-flex items-center gap-1 px-3 py-1.5 border border-[#D7DCE4] text-sm rounded-xl hover:bg-white"
                    data-testid={`edit-approval-${it.id}`}
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                    {editingId === it.id ? 'Cancel edit' : 'Edit'}
                  </button>
                  <button
                    onClick={() => decide(it.id, 'reject')}
                    disabled={busyId === it.id}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-[#CE3636] hover:bg-[#FFEBE9] text-sm rounded-xl ml-auto"
                    data-testid={`reject-${it.id}`}
                  >
                    <X className="w-3.5 h-3.5" />
                    Reject
                  </button>
                </div>
              )}
            </article>
          ))
        )}
        </div>
      </div>
    </div>
  );
}

// =============================================================
// Post approval card — Notion-inspired layout
// Clean, generous spacing · monochrome with selective accents ·
// page-block aesthetic with inline metadata, callout-style status,
// and a minimal divider before action row.
// =============================================================

const TYPE_EMOJI = {
  text: '📝',
  photo: '🖼️',
  video: '🎬',
  document: '📎',
  article: '📰',
  poll: '📊',
};

function PostApprovalCard({
  p,
  tab,
  busyId,
  rejectingPostId,
  postRejectComment,
  setRejectingPostId,
  setPostRejectComment,
  onDecide,
}) {
  const isRejecting = rejectingPostId === p.id;
  const isBusy = busyId === p.id;
  const apiBase = process.env.REACT_APP_BACKEND_URL || '';
  const mediaUrl = (p.has_image || p.has_video || p.has_document)
    ? `${apiBase}/api/approvals/posts/${p.id}/media`
    : null;
  const downloadUrl = mediaUrl ? `${mediaUrl}?download=1` : null;

  const StatusPill = () => {
    if (tab === 'pending') return null;
    const map = {
      published:  { bg: '#F4F4F2', fg: '#191919', dot: '#1E7F4E', label: 'Published' },
      approved:   { bg: '#F4F4F2', fg: '#191919', dot: '#0052CC', label: 'Approved · scheduled' },
      rejected:   { bg: '#F4F4F2', fg: '#9B9B9B', dot: '#9B9B9B', label: 'Rejected' },
      failed:     { bg: '#FFEBE9', fg: '#CE3636', dot: '#CE3636', label: 'Failed to publish' },
    };
    const cfg = map[p.status] || { bg: '#F4F4F2', fg: '#6B6B6B', dot: '#6B6B6B', label: p.status };
    return (
      <span className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-md" style={{ background: cfg.bg, color: cfg.fg }}>
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: cfg.dot }} />
        {cfg.label}
      </span>
    );
  };

  return (
    <article
      className="group bg-white rounded-lg transition-colors"
      data-testid={`post-approval-${p.id}`}
    >
      {/* Notion-style page header: emoji + title + inline metadata */}
      <div className="px-1 py-1">
        <div className="flex items-start gap-3 mb-3">
          <span className="text-2xl leading-none mt-0.5 select-none">{TYPE_EMOJI[p.post_type] || '📝'}</span>
          <div className="min-w-0 flex-1">
            <h3 className="text-[17px] font-semibold text-[#191919] tracking-tight">
              {p.article_title || (p.text ? p.text.split('\n')[0].slice(0, 80) + (p.text.length > 80 ? '…' : '') : 'Untitled post')}
            </h3>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-[#6B6B6B]">
              <span className="inline-flex items-center gap-1">
                <FileText className="w-3 h-3" /> {p.post_type}
              </span>
              <span className="inline-flex items-center gap-1">
                {p.visibility === 'connections' ? <Users className="w-3 h-3" /> : <Globe className="w-3 h-3" />}
                {p.visibility === 'connections' ? 'Connections' : 'Anyone'}
              </span>
              {p.scheduled_at && (
                <span className="inline-flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> {formatApiDateTime(p.scheduled_at)}
                </span>
              )}
              {p.created_by_admin_email && (
                <span>Drafted by {p.created_by_admin_email}</span>
              )}
              <StatusPill />
            </div>
          </div>
        </div>

        {/* Body — Notion text block */}
        <div className="text-[15px] leading-[1.65] text-[#191919] whitespace-pre-wrap py-1">
          {p.text || <span className="text-[#9B9B9B] italic">No body text</span>}
        </div>

        {p.link_url && (
          <a
            href={p.link_url}
            target="_blank"
            rel="noreferrer"
            className="mt-3 inline-flex items-center gap-1.5 text-[13px] text-[#191919] hover:bg-[#F7F7F5] -mx-1 px-2 py-1 rounded-md border border-[#E3E2E0]"
          >
            <ExternalLink className="w-3.5 h-3.5 text-[#6B6B6B]" />
            <span className="truncate max-w-md">{p.link_url}</span>
          </a>
        )}

        {/* Image / video — Notion's column-block treatment */}
        {mediaUrl && p.has_image && (
          <div className="mt-4">
            <img src={mediaUrl} alt="" className="w-full max-h-[420px] object-cover rounded-md" />
          </div>
        )}
        {mediaUrl && p.has_video && (
          <div className="mt-4">
            <video src={mediaUrl} controls className="w-full max-h-[420px] rounded-md bg-black" />
          </div>
        )}
        {mediaUrl && p.has_document && (
          <a
            href={mediaUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-4 flex items-center gap-3 border border-[#E3E2E0] rounded-md p-3 hover:bg-[#F7F7F5] transition-colors"
          >
            <div className="w-10 h-12 rounded-md bg-[#FFF4E5] border border-[#FFD9A8] flex items-center justify-center">
              <FileText className="w-5 h-5 text-[#9A3412]" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-medium text-[#191919] truncate">{p.document_name || 'Document'}</p>
              <p className="text-[11px] text-[#6B6B6B]">Open document</p>
            </div>
            <ExternalLink className="w-4 h-4 text-[#9B9B9B]" />
          </a>
        )}

        {/* Poll — Notion-callout style */}
        {p.post_type === 'poll' && p.poll_options && (
          <div className="mt-4 border border-[#E3E2E0] rounded-md p-4 bg-[#FBFBFA]">
            <p className="text-[12px] text-[#6B6B6B] mb-3 flex items-center gap-1.5">
              <BarChart2 className="w-3.5 h-3.5" />
              Poll · runs for {p.poll_duration_days || 7} {p.poll_duration_days === 1 ? 'day' : 'days'}
            </p>
            <ul className="space-y-1.5">
              {p.poll_options.map((opt, i) => (
                <li key={i} className="text-[14px] text-[#191919] py-2 px-3 bg-white border border-[#E3E2E0] rounded-md">
                  {opt}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Status / outcome callouts (Notion-style, left-bordered) */}
        {p.publish_error && (
          <div className="mt-4 flex items-start gap-2 border-l-2 border-[#CE3636] bg-[#FFF4F2] py-2 px-3 rounded-r-md">
            <AlertTriangle className="w-4 h-4 text-[#CE3636] mt-0.5 shrink-0" />
            <div>
              <p className="text-[12px] font-medium text-[#CE3636]">Publishing failed</p>
              <p className="text-[12px] text-[#7A1F1F]">{p.publish_error}</p>
            </div>
          </div>
        )}
        {p.rejection_comment && (
          <div className="mt-4 flex items-start gap-2 border-l-2 border-[#CE3636] bg-[#FFF4F2] py-2 px-3 rounded-r-md">
            <MessageSquare className="w-4 h-4 text-[#CE3636] mt-0.5 shrink-0" />
            <div>
              <p className="text-[12px] font-medium text-[#CE3636]">Your change request</p>
              <p className="text-[12px] text-[#7A1F1F] whitespace-pre-wrap">{p.rejection_comment}</p>
            </div>
          </div>
        )}
        {p.published_url && (
          <a
            href={p.published_url}
            target="_blank"
            rel="noreferrer"
            className="mt-4 inline-flex items-center gap-1.5 text-[12px] text-[#0052CC] hover:underline"
          >
            <ExternalLink className="w-3 h-3" /> View on LinkedIn
          </a>
        )}
      </div>

      {/* Action row — separated by Notion-style hairline */}
      {tab === 'pending' && (
        <>
          <div className="my-5 h-px bg-[#E3E2E0]" />
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 px-1 pb-1">
            <button
              onClick={() => onDecide(p.id, 'approve')}
              disabled={isBusy}
              className="inline-flex items-center justify-center gap-1.5 h-9 px-3 bg-[#12C8C6] hover:bg-[#0FB3B1] text-white text-[13px] font-medium rounded-xl disabled:opacity-50"
              data-testid={`approve-post-${p.id}`}
            >
              {isBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              {p.scheduled_at ? 'Approve · publish at scheduled time' : 'Approve & publish'}
            </button>
            <button
              onClick={() => {
                setRejectingPostId(isRejecting ? null : p.id);
                setPostRejectComment('');
              }}
              disabled={isBusy}
              className="inline-flex items-center justify-center gap-1.5 h-9 px-3 text-[13px] text-[#CE3636] border border-[#FF8080] hover:bg-[#FFEBE9] rounded-md disabled:opacity-50 sm:ml-auto"
              data-testid={`reject-post-${p.id}`}
            >
              <X className="w-3.5 h-3.5" /> Request changes
            </button>
          </div>
          {isRejecting && (
            <div className="mt-3 rounded-lg border border-[#E3E2E0] bg-[#FBFBFA] p-3">
              <label className="block text-xs font-medium text-[#191919] mb-1.5">
                What should your team change?
              </label>
              <textarea
                value={postRejectComment}
                onChange={(e) => setPostRejectComment(e.target.value)}
                rows={4}
                className="w-full bg-white border border-[#D7DCE4] rounded-xl text-sm p-3 focus:outline-none focus:ring-2 focus:ring-[#12C8C6]/20 resize-y"
                placeholder="Example: Please use the second image, make the tone more formal, and target connections only."
                data-testid={`reject-post-comment-${p.id}`}
              />
              <div className="mt-2 flex flex-col sm:flex-row gap-2 sm:justify-end">
                <button
                  onClick={() => { setRejectingPostId(null); setPostRejectComment(''); }}
                  className="h-9 px-3 text-sm text-[#6B6B6B] hover:bg-[#F0F0EF] rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={() => onDecide(p.id, 'reject', postRejectComment)}
                  disabled={isBusy || !postRejectComment.trim()}
                  className="h-9 px-3 bg-[#CE3636] text-white text-sm font-medium rounded-lg disabled:opacity-50"
                  data-testid={`confirm-reject-post-${p.id}`}
                >
                  Send request
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </article>
  );
}
